/**
 * The environment, parsed once, or the app does not start.
 *
 * F-009. Every `VITE_*` variable used to be read ad hoc at the point of use, with no schema and no
 * check. Two failures followed from that, and both were silent:
 *
 *   · `src/utils/api.ts` fell back to a hard-coded localhost URL when `VITE_API_URL` was unset, so
 *     a production build with a missing variable shipped, loaded, and then pointed every request
 *     at the operator's own machine. The console showed connection errors, not a configuration
 *     fault.
 *   · `src/config/firebase.ts` passed whatever it found straight to `initializeApp`, so a missing
 *     key surfaced later as `auth/invalid-api-key` on the login screen — which
 *     `src/hooks/useAuth.tsx` then reports as "the API is down" (F-007).
 *
 * Parsing here turns both into one line naming the variables, at startup, before anything renders.
 *
 * ⚠️ **NOT ZOD, AND THE TICKET ASKED FOR ZOD.** Measured, because the ticket could not have known:
 * this module is imported by `src/main.tsx`, so whatever it imports lands in the entry chunk. Zod
 * was previously reached only through `src/contracts/`, which every feature loads lazily. Adding
 * `import { z } from 'zod'` here took the entry chunk from **11.7 kB to 893.1 kB raw** — the whole
 * library, on first paint, to check that ten strings are non-empty. Ten lines of hand-rolled
 * checking costs nothing and names every offender, which is all the schema was for.
 *
 * ⚠️ This module must stay the ONLY reader of `import.meta.env`. That is what makes it the whole
 * truth rather than one opinion among several, and a guard asserts it.
 */

/**
 * Required at boot. A missing one of these is a deployment fault, not a degraded feature.
 *
 * ⚠️ THIS LIST WAS TOO LONG AND IT TOOK DOWN backoffice.dev ON 2026-09-07.
 * It originally read "the six `initializeApp` actually needs", which was wrong on two counts: the
 * argument is what the APP needs, not what the config object has fields for, and this app uses
 * exactly one Firebase service. `src/config/firebase.ts` imports `firebase/compat/auth` and nothing
 * else; `grep -rhoE "firebase\.[a-z]+\(\)" src/` returns three `firebase.auth()` and no others.
 *
 * So `storageBucket` (Cloud Storage) and `messagingSenderId` (FCM) configure services that are never
 * initialised. Requiring them meant a correct environment — one that simply does not provision
 * services the app cannot reach — was refused at boot. The check was right to be strict and wrong
 * about what it was strict on.
 *
 * The test for this list is **"can the app start and function without it?"**, not "does Firebase
 * document it".
 */
const REQUIRED = [
    'VITE_API_URL',
    // Firebase Auth is the only service this app initialises, and these are what it needs.
    'VITE_APP_FIREBASE_API_KEY',
    'VITE_APP_FIREBASE_AUTH_DOMAIN',
    'VITE_APP_FIREBASE_PROJECT_ID',
    'VITE_APP_FIREBASE_APP_ID',
] as const;

/** Absent by design in some environments; the feature degrades rather than the app failing. */
const OPTIONAL = [
    'VITE_SENTRY_DSN',
    'VITE_SENTRY_ENVIRONMENT',
    'VITE_APP_GOOGLE_MAPS_API_KEY',
] as const;

export type Env = Readonly<Record<(typeof REQUIRED)[number], string>> &
    Readonly<Partial<Record<(typeof OPTIONAL)[number], string>>>;

/** Vite substitutes an unset variable as `undefined`; a bare `KEY=` line in .env yields `''`. */
const present = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/**
 * Exported so the guard can exercise a failing record without crashing the runner.
 *
 * The message names **every** offending variable rather than the first, because a boot failure
 * reading "invalid configuration" costs the reader the one thing they needed, and fixing them one
 * restart at a time costs the rest.
 */
export const parseEnv = (source: unknown): Env => {
    const raw = (source ?? {}) as Record<string, unknown>;
    const missing = REQUIRED.filter((name) => !present(raw[name]));

    if (missing.length > 0) {
        throw new Error(
            `Configuración inválida: ${[...missing].sort().join(', ')}. ` +
                `Revisá el .env de este entorno; .env.example lista las claves que la app necesita.`,
        );
    }

    const parsed: Record<string, string> = {};
    for (const name of REQUIRED) parsed[name] = String(raw[name]).trim();
    for (const name of OPTIONAL) if (present(raw[name])) parsed[name] = raw[name].trim();

    return Object.freeze(parsed) as Env;
};

export const env = parseEnv(import.meta.env);
